import { useState } from 'react';
import {
  Stack, Button, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Autocomplete, Alert, Snackbar, Typography,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { clientPortalApi } from '../../api/clientPortal.api';
import { dealerOrdersApi, type DealerOrder } from '../../api/dealerOrders.api';
import { evcpdProductsApi, type EvcpdProduct } from '../../api/evcpdProducts.api';
import { extractApiError } from '../../api/error';

const inr = (x = 0) => `₹${Number(x).toLocaleString('en-IN')}`;
const human = (s?: string | null) => (s ? s.replace(/_/g, ' ') : '—');

export function ClientOrderPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['client-orders', page, limit], queryFn: () => dealerOrdersApi.list({ page, limit }) });

  const columns: Column<DealerOrder>[] = [
    { key: 'orderCode', label: 'Order', width: 160 },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={human(r.status)} /> },
    { key: 'grandTotal', label: 'Total', render: (r) => inr(r.grandTotal) },
    { key: 'orderDate', label: 'Order Date', render: (r) => r.orderDate ?? '—' },
    { key: 'createdAt', label: 'Created', render: (r) => new Date(r.createdAt).toLocaleDateString() },
  ];

  return (
    <>
      <PageHeader title="My Orders" subtitle="Place and track orders (only products mapped to your account are available)."
        action={<Button startIcon={<Add />} variant="contained" onClick={() => setOpen(true)}>New Order</Button>} />
      <DataTable columns={columns} rows={data?.data ?? []} loading={isLoading} page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id} />
      {open && <NewOrderDialog onClose={() => setOpen(false)} onDone={() => { setOpen(false); setToast('Order placed'); qc.invalidateQueries({ queryKey: ['client-orders'] }); }} />}
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}

interface Line { product: EvcpdProduct | null; quantity: number }

function NewOrderDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [dealerId, setDealerId] = useState<number | ''>('');
  const [lines, setLines] = useState<Line[]>([{ product: null, quantity: 1 }]);
  const [error, setError] = useState<string | null>(null);

  const { data: dealers } = useQuery({ queryKey: ['client-portal-dealers'], queryFn: () => clientPortalApi.dealers() });
  const { data: products } = useQuery({ queryKey: ['client-catalogue'], queryFn: () => evcpdProductsApi.list({ limit: 500 }) });

  const mut = useMutation({
    mutationFn: () => dealerOrdersApi.create({
      dealerId: Number(dealerId),
      items: lines.filter((l) => l.product).map((l) => ({ productId: l.product!.id, quantity: l.quantity })),
    }),
    onSuccess: onDone,
    onError: (e) => setError(extractApiError(e)),
  });

  const canSubmit = dealerId && lines.some((l) => l.product && l.quantity > 0);

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>New Order</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField select label="Dealer" value={dealerId} onChange={(e) => setDealerId(Number(e.target.value))} fullWidth>
            {(dealers ?? []).map((d) => <MenuItem key={d.dealer_id} value={d.dealer_id}>{d.dealer_name} ({d.dealer_code})</MenuItem>)}
          </TextField>
          <Typography variant="subtitle2">Products</Typography>
          {lines.map((line, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="center">
              <Autocomplete sx={{ flex: 1 }} options={products?.data ?? []} getOptionLabel={(o) => `${o.name} (${o.productCode})`}
                isOptionEqualToValue={(o, v) => o.id === v.id} value={line.product}
                onChange={(_e, v) => setLines(lines.map((l, idx) => (idx === i ? { ...l, product: v } : l)))}
                renderInput={(p) => <TextField {...p} size="small" label="Product" />} />
              <TextField size="small" type="number" label="Qty" value={line.quantity} sx={{ width: 90 }}
                onChange={(e) => setLines(lines.map((l, idx) => (idx === i ? { ...l, quantity: Number(e.target.value) } : l)))} />
              <IconButton size="small" color="error" disabled={lines.length === 1} onClick={() => setLines(lines.filter((_, idx) => idx !== i))}><Delete fontSize="small" /></IconButton>
            </Stack>
          ))}
          <Button size="small" startIcon={<Add />} onClick={() => setLines([...lines, { product: null, quantity: 1 }])}>Add line</Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit || mut.isPending} onClick={() => mut.mutate()}>Place Order</Button>
      </DialogActions>
    </Dialog>
  );
}
